import { LBound, LNodeHash, LNode, LPoint, IGraph, IRoutes } from "./types";

export default class Graph implements IGraph {
	private nodes: LNode[];
	public hash: LNodeHash;
	public routes: IRoutes;

	private _root: LNode;
	private _deep: LNode[][];
	public _bounds: LBound[];
	public _tbounds: LBound;
	public _width: number;
	private _leaves: LNode[];
	public _symmetry: boolean;
	private _ybounds: LBound;

	constructor(parent?: IGraph, root?: LNode, config?) {
		this._tbounds = [0, 0];

		if (!parent) {
			this.nodes = [];
			this.hash = {};
		} else {
			this.nodes = [root];
			parent.collectSubNodes(root, this.nodes);
			this.hash = parent.hash;
			this.root(root, config);
		}
	}

	public copy() {
		const t = new Graph();
		t.nodes = this.nodes.map(({ id, w, h, x, y, isn, iss }) => ({
			id,
			w,
			h,
			x,
			y,
			kids: [],
			links: [],
			isn,
			iss,
		}));

		t.hash = {};
		t.nodes.forEach(a => {
			t.hash[a.id] = a;
		});
		t.nodes.forEach(a => {
			a.kids = this.hash[a.id].kids.map(b => t.hash[b.id]);
			a.links = this.hash[a.id].links.map(b => t.hash[b.id]);
		});
		t._tbounds = [].concat(this._tbounds);
		t._ybounds = [].concat(this._ybounds);
		t._bounds = this._bounds ? this._bounds.map(a => [].concat(a)) : [];
		t.routes = this.routes;
		t._root = this._root ? t.hash[this._root.id] : null;

		return t;
	}

	public collectSubNodes(root: LNode, backet: LNode[]) {
		for (let i = 0; i < root.kids.length; i++) {
			const e = root.kids[i];
			backet.push(e);
			if (e.kids) this.collectSubNodes(e, backet);
		}
	}

	public getLevelBounds(): LBound[] {
		return this._bounds;
	}

	public getBounds(i: number, padding: number, wide: boolean): number[] {
		let b: LBound;
		if (wide) {
			b = this._tbounds;
		} else {
			b = this._bounds[i];
		}

		return [b[0] ? b[0] - padding / 4 : 0, b[1] ? b[1] + padding / 4 : 0];
	}

	public addEdge(from: string, to: string) {
		const f = this.hash[from];
		const t = this.hash[to];

		f.links.push(t);
		t.links.push(f);
	}

	public importNodes(node: LNode[]) {
		node.forEach(a => {
			this.hash[a.id] = a;
			this.nodes.push(a);
		});
	}

	public split(filter) {
		const next = [];
		this.nodes = this.nodes.filter(a => {
			if (!filter(a)) return true;

			delete this.hash[a.id];
			next.push(a);
			return false;
		});

		if (!next.length) return null;

		const g2 = new Graph();
		g2.importNodes(next);

		return g2;
	}

	public addNode(node: LNode) {
		node.kids = [];
		node.links = [];

		if (!this._root) this._root = node;

		this.hash[node.id] = node;
		this.nodes.push(node);
	}

	public getNode(id: string): LNode {
		return this.hash[id];
	}

	public getRoot(): LNode {
		return this._root;
	}

	public getLevels(): LNode[][] {
		return this._deep;
	}

	public clean() {
		this.nodes.forEach(a => {
			if (a.links.length > 1) {
				const were = [];
				a.links = a.links.filter(b => {
					if (were.includes(b) || a === b) {
						return false;
					}
					were.push(b);
					return true;
				});
			}
		});
	}

	public getNodes(): LNode[] {
		return this.nodes;
	}

	public getBox(): [LBound, LBound] {
		return [this._tbounds, this._ybounds];
	}

	public setGlobalBox() {
		if (!this.nodes.length) {
			return;
		}

		let cmx, cx, cmy, cy;
		cmx = cmy = Infinity;
		cx = cy = -Infinity;

		this.nodes.forEach(a => {
			const mx = a.x - a.w / 2;
			const x = a.x + a.w / 2;
			const my = a.y - a.h / 2;
			const y = a.y + a.h / 2;

			if (mx < cmx) cmx = mx;
			if (my < cmy) cmy = my;
			if (x > cx) cx = x;
			if (y > cy) cy = y;
		});

		this._tbounds = [cmx, cx];
		this._ybounds = [cmy, cy];
	}

	public translate(p: LPoint) {
		this.nodes.forEach(n => {
			n.x += p.x;
			n.y += p.y;
		});

		this._tbounds[0] += p.x;
		this._tbounds[1] += p.x;

		if (this._bounds)
			for (let i = 0; i < this._bounds.length; i++) {
				this._bounds[i][0] += p.x;
				this._bounds[i][1] += p.x;
			}

		if (this._ybounds) {
			this._ybounds[0] += p.y;
			this._ybounds[1] += p.y;
		}
	}

	public rotate(a: LPoint) {
		// [x: sin, b: cos ]
		const sin = a.x;
		const cos = a.y;

		this.nodes.forEach(n => {
			const x = n.x * cos - n.y * sin;
			const y = n.x * sin + n.y * cos;

			n.x = x;
			n.y = y;
		});

		const [xmin, xmax] = this._tbounds;
		const [ymin, ymax] = this._ybounds;
		this._tbounds = [xmin * cos - ymin * sin, xmax * cos - ymax * sin].sort();
		this._ybounds = [xmin * sin + ymin * cos, xmax * sin + ymax * cos].sort();
	}
	public setBox() {
		const last = this._deep[this._deep.length - 1];
		let max = -Infinity;
		last.forEach(a => {
			const test = a.y + a.h / 2;
			if (test > max) max = test;
		});
		this._ybounds = [this._root.y - this._root.h / 2, max];
	}
	public mirror() {
		this.nodes.forEach(n => {
			n.x = -n.x;
		});

		for (let i = 0; i < this._bounds.length; i++) {
			const [l, u] = this._bounds[i];
			this._bounds[i] = [-u, -l];
		}
		const [l, u] = this._tbounds;
		this._tbounds = [-u, -l];
	}

	public nonLeaves(n: LNode) {
		return n.kids.filter(a => a.kids.length > 0);
	}

	public toTree(root) {
		if (!root || !this.hash[root.id]) root = this._detectRoot();
		this.nodes.forEach(a => (a.kids = []));

		this.setKids(root);
		return root;
	}

	public root(root, config?) {
		this._deep = [[root]];

		const hw = Math.round((config && config.rotate ? root.h : root.w) / 2);
		this._tbounds = [-hw, hw];
		this._bounds = [[-hw, hw]];

		this._leaves = [];
		this._root = root;

		this._setLevels(root, 1);
		this._width = 1;
		for (let i = 0; i < this._deep.length; i++) {
			const n = this._deep[i].length;
			if (n > this._width) this._width = n;
		}
	}

	private setKids(root) {
		root.links.forEach(a => {
			if (!a.kids.length) {
				root.kids.push(a);
				this.setKids(a);
			}
		});
	}
	private _detectRoot() {
		const ext = {};
		let nodes: any[] = this.nodes;
		let round = [];
		do {
			round.forEach(k => (ext[k] = 1));
			round = [];

			nodes = nodes.filter(a => {
				const check = a.links.map(a => (ext[a.id] ? 0 : 1)).reduce((a, b) => a + b, 0) > 1;

				if (!check) round.push(a.id);
				return check;
			});
		} while (nodes.length > 2 && round.length);

		return nodes[0] || this.nodes[0];
	}
	private _setLevels(node, l) {
		const kids = node.kids;
		for (let i = 0; i < kids.length; i++) {
			const n = kids[i];

			const level = this._deep[l];
			if (!level) {
				this._deep[l] = [n];
				this._bounds[l] = [0, 0];
			} else level.push(n);

			if (n.kids.length) this._setLevels(n, l + 1);
			else this._leaves.push(n);
		}
	}
	public getIString(): string {
		const levels: string[] = [];

		// mark leaves
		for (let i = 0; i < this._leaves.length; i++) {
			this._leaves[i].isn = 0;
			this._leaves[i].iss = "";
		}

		// for each level
		for (let i = this._deep.length - 2; i >= 0; i--) {
			// for each non leave
			const level = this._deep[i].filter(a => a.kids.length > 0);
			for (let j = 0; j < level.length; j++) {
				// create i-string for all non-leaves
				const node = level[j];
				for (let k = 0; k < node.kids.length; k++) {
					node.iss = node.kids
						.map(n => n.isn)
						.sort()
						.join(",");
				}
			}

			// sort non-leaves to form level i-string
			level.sort((a, b) => (a.iss > b.iss ? 1 : -1));
			levels.push(level.map(a => a.iss).join("|"));

			// // sort all nodes by i-string
			// const all = this._deep[i];
			// all.sort((a,b) => (a.iss > b.iss ? 1 : -1));
			// assign i-numbers based on i-strings
			let code = level[0].iss;
			let num = 1;
			for (let j = 0; j < level.length; j++) {
				if (code !== level[j].iss) {
					code = level[j].iss;
					num++;
				}
				level[j].isn = num;
			}
		}

		// return tree i-string
		return levels.join(";");
	}
}
